import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { AdminSignOrderService, AdminOrderListEntry } from '../../services/admin-sign-order.service';
import { formatSignOrderDate } from '../../sign-orders/sign-order-date.utils';

@Component({
  selector: 'app-admin-sign-order-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NzAlertComponent,
    NzButtonModule,
    NzDatePickerModule,
    NzIconModule,
    NzInputModule,
    NzPaginationModule,
    NzSelectModule,
    NzSpinModule,
    NzTableModule,
    NzTagModule,
    TranslateModule,
  ],
  templateUrl: './sign-order-list.component.html',
  styleUrls: ['./sign-order-list.component.less'],
})
export class AdminSignOrderListComponent implements OnInit, OnDestroy {
  readonly formatSignOrderDate = formatSignOrderDate;

  orders: AdminOrderListEntry[] = [];
  page = 1;
  pageSize = 20;
  total = 0;
  isLoading = true;
  errorMessage: string | null = null;

  search = '';
  statusFilter: string | null = null;
  dateFrom: Date | null = null;
  dateTo: Date | null = null;

  private search$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private adminService: AdminSignOrderService,
    public translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.search$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.page = 1;
      this.loadOrders();
    });
    this.loadOrders();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(): void {
    this.search$.next();
  }

  onFilterChange(): void {
    this.page = 1;
    this.loadOrders();
  }

  loadOrders(page?: number): void {
    if (page) this.page = page;
    this.isLoading = true;
    this.errorMessage = null;

    const params: any = {
      page: this.page,
      pageSize: this.pageSize,
    };
    if (this.search) params.search = this.search;
    if (this.statusFilter) params.status = this.statusFilter;
    if (this.dateFrom) params.dateFrom = this.dateFrom.toISOString();
    if (this.dateTo) params.dateTo = this.dateTo.toISOString();

    this.adminService.listOrders(params).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.orders = res.items;
        this.total = res.total;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = this.translate.instant('SIGN_ORDER.ADMIN.ERROR_LOADING');
      },
    });
  }

  formatCents(cents: number, currency: string): string {
    const lang = this.translate.currentLang === 'de' ? 'de-DE' : 'en-US';
    const amount = (cents / 100).toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${amount} ${currency}`;
  }

  localizedText(text: { de: string; en: string; [key: string]: string } | undefined): string {
    if (!text) return '';
    const lang = this.translate.currentLang || 'de';
    return text[lang] || text.de || text.en || '';
  }

  getStatusLabel(status: string): string {
    return this.translate.instant(`SIGN_ORDER.STATUS.${status.toUpperCase()}`);
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      pending_payment: 'orange',
      payment_received: 'blue',
      processing: 'processing',
      ordered_from_supplier: 'cyan',
      received_from_supplier: 'geekblue',
      shipped: 'purple',
      completed: 'green',
      cancelled: 'red',
    };
    return colors[status] || 'default';
  }
}
